import { type CollisionDetection, closestCorners, rectIntersection } from '@dnd-kit/core';

/**
 * Because our table has both draggable columns and rows, we need custom collision detection. Otherwise when dragging columns, we get buggy behaviour as dnd-kit considers the row-elements underneath as drop areas.
 *
 * The function first checks for collisions with any nodes that are in our column list to avoid the above issue, and alternatively
 * checks the closestCorners if no colissions happening. This is also needed because technically only or <th> are drop-zones,
 * yet to the user they also expect the <td> cells in the column to trigger drop-indicators. Finally, if the closest collision is more than 100px away from the pointer, it returns an empty array to enable the 'drag-too-far-to-cancel' ux.
 *
 * @param columnIds - The allowed droppable IDs for header columns.
 */
export function forgivingHeaderColumnsCollisions(columnIds: string[]): CollisionDetection {
    return (args) => {
        // First, try strict collision detection.
        let collisions = rectIntersection(args) || [];
        collisions = collisions.filter((collision) => columnIds.includes(collision.id.toString()));

        // If no collisions are found, use closestCorners.
        if (collisions.length === 0) {
            collisions = closestCorners(args) || [];
            collisions = collisions.filter((collision) =>
                columnIds.includes(collision.id.toString())
            );
        }

        // If we have pointerCoordinates and at least one collision,
        // compute the distance from the pointer to each collision's droppable rect.
        if (args.pointerCoordinates && collisions.length > 0) {
            let minDistance = Number.POSITIVE_INFINITY;
            for (const collision of collisions) {
                const container = args.droppableContainers.find(
                    (c) => c.id.toString() === collision.id.toString()
                );
                if (container?.rect.current) {
                    const rect = container.rect.current;
                    const { x: px, y: py } = args.pointerCoordinates;
                    // Compute horizontal distance: if pointer is inside rect horizontally, dx is 0.
                    const dx = Math.max(rect.left - px, 0, px - rect.right);
                    // Similarly for vertical.
                    const dy = Math.max(rect.top - py, 0, py - rect.bottom);
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    if (distance < minDistance) {
                        minDistance = distance;
                    }
                }
            }
            // If the closest collision is more than 100px away, return no collisions.
            if (minDistance > 100) {
                return [];
            }
        }
        return collisions;
    };
}
